<template>
  <div v-if="loading">
    <div style="height: 90vh" v-loading="true"></div>
  </div>
  <div v-else>
    <div class="flex align-center mb-4">
      <div class="flex-grow">
        <FlowHeader v-if="!isDraft && tradingInfo" :tradingInfo="tradingInfo" />
      </div>
      <div class="process-show-switch-form">
        <label>Process</label>
        <el-switch
          class="process-show-switch"
          v-model="processVisible"
          active-color="#5185C5"
          active-icon-class="tw-icon-view"
          inactive-icon-class="tw-icon-not-view"
        ></el-switch>
      </div>
    </div>

    <div class="tradingFlowName mb-2">
      <div
        class="flex align-center mb-4 pb-4"
        style="border-bottom: 1px solid #eee;"
      >
        <div class="flex-grow flex align-end text-gray-800">
          <div class="text-24 text-bold">
            {{ pageTitle }}
          </div>
          <div v-if="pageDesc" class="text-18 ml-4">( {{ pageDesc }} )</div>
        </div>
        <div>
          <TwButton @click="handleCancel">Cancel</TwButton>
          <TwButton v-if="isDraft && !isFwd" type="primary" @click="handleSave"
            >Save</TwButton
          >
          <TwButton type="primary" @click="handleRegister">Register</TwButton>
        </div>
      </div>

      <!-- 下書き時の入力 -->
      <div v-if="isDraft">
        <el-form
          v-model="formTradingName"
          @submit.native.prevent
          label-width="120px"
          label-position="left"
        >
          <el-form-item :label="$t(`Label.Trading Name`)" prop="tradingName">
            <el-input
              v-model="formTradingName.tradingName"
              show-word-limit
              maxlength="100"
              style="max-width: 450px"
            />
          </el-form-item>
        </el-form>
      </div>

      <!-- プロセス一覧 -->
      <div class="flex align-end my-3">
        <el-form
          :model="formFlowName"
          ref="formComponent"
          class="flex-grow"
          :rules="rules"
          label-width="120px"
          label-position="left"
        >
          <el-form-item :label="$t(`List.Flow Name`)" prop="tradingManageName">
            <el-input
              style="width: 450px"
              v-model="formFlowName.tradingManageName"
              @input="$emit('dirty')"
              show-word-limit
              maxlength="100"
            />
          </el-form-item>

         <div class="flex flex-wrap">
            <!-- フローReference -->
            <el-form-item
              :label="$t(`Label.Flow Reference`)"
              prop="flowReference"
              style="display: inline-block; margin-right: 10px; width: 500px;"
              >
              <el-input
                style="width: 350px"
                v-model="formFlowName.flowReference"
                @input="$emit('dirty')"
                show-word-limit
                maxlength="100"
              />
            </el-form-item>
            <!-- フロー担当者 -->
            <el-form-item
              :label="$t(`Label.Flow PIC`)"
              prop="flowUserName"
              style="display: inline-block; width: 620px;"
              >
              <el-input
                style="width: 350px"
                v-model="formFlowName.flowUserName"
                @input="$emit('dirty')"
                show-word-limit
                maxlength="100"
              />
              <tw-button style="margin-left: 10px" type="secondary" size="small" @click="assignMe">Assign Me</tw-button>
            </el-form-item>
        </div>

        </el-form>
        <AddProcessCascader
          v-if="processList && !isFwd"
          :flowInfo="tmpFlowInfo"
          :processList="processList"
          @select-process="addProcess"
        />
      </div>
    </div>
    <div class="mb-6">
      <el-form :model="formData" ref="formProcessComponent">
        <DetailProcessFormTable
          :loading="editorLoading"
          :cols="filteredSchema"
          :tableData="processData"
          :formData="formData"
          :flowInfo="tmpFlowInfo"
          :editMode="true"
          @update-prop="updateFlowValue"
          @click-delete-process="removeProcess"
        />
      </el-form>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import {
  defineComponent,
  ref,
  reactive,
  toRef,
  watch,
  computed,
  onMounted,
} from '@vue/composition-api';
import {
  EDITABLE_FLG,
  SECTION_PREFIX,
  TRADING_PROGRESS_STATUS,
  DELETE_ABLE_FLG,
  COPYABLE_FLG,
  DETAIL_VIEWABLE_FLG,
  PROCESS_CANCEL_FLG,
  TRADING_REGISTRATION_STATUS,
  COMPANY_ROLE_CD,
  IS_OWNER_FLG,
  VISIBLE_FLG,
  ABLE_FLG,
  OTHER_TRADING_FLG,
  PROCESS_PROGRESS_STATUS,
  LANGUAGE_CODE,
} from 'lib-tw-common';

import {
  generateUuid,
  orderEntities,
  MAX_PROCESS_NUMBER,
  mapToSchemaList,
} from '@/utils/searchUtil.js';
import { orderProcesses } from '@/utils/entity.js';
import { processNames as processNamesMap } from '@/dictionaries/map.js';

import { $api } from '@/store/ApiClient';
import { MAX_TRADING_FLOW_COUNT } from '@/utils/constants';

// schema
import { tradingDetailTableFormNewSchema } from '@/dictionaries/vue3/tradingDetailTableFormNewSchema.js';

import { useBffTradingManagesSearch } from '@/composables/useBffTradingManagesSearch';
import { useBffTradingManagesEditor } from '@/composables/useBffTradingManagesEditor';
import { useLakeelValidation } from '@/composables/useLakeelValidation';

import TwButton from '@/components/atoms/TwButton.vue';

import AddProcessCascader from './AddProcessCascader.vue';
import DetailProcessFormTable from './DetailProcessFormTable.vue';
import FlowHeader from './FlowHeader.vue';

export default defineComponent({
  components: {
    TwButton,
    DetailProcessFormTable,
    FlowHeader,
    AddProcessCascader,
  },

  setup(props, { root, emit }) {
    const language = root.$store.state.locale;
    const lang = _.get(LANGUAGE_CODE, `${language}`, 'ja');

    // const tradingPatternId = ref('');
    // const companyId = ref('');
    const formComponent = ref(null);
    const formProcessComponent = ref(null);
    const formData = reactive({});

    const formTradingName = reactive({ tradingName: '' });
    const formFlowName = reactive({
      tradingManageName: '',
      flowReference: '',
      flowUserName: ''
    });

    const tmpFlowInfo = ref(null);
    const processData = ref([]);
    const deleteTradingFlowIds = ref([]);
    const isDirty = ref(false);
    const isDraft = ref(false);

    const processVisible = ref(true);

    const tradingId = root.$route?.params.tradingId;

    // companyId取得
    const companyId = toRef(root.$store.state, 'companyId');

    // 取引フロー詳細のコピーボタンからの遷移時等に利用
    const copyId = computed(() => root.$route.query.cid);

    // 新規取引の場合はセッションストレージにtradingPatternやtradingNameを持つ
    const key = root.$route.query.p;
    const patternInfo = key ? JSON.parse(sessionStorage.getItem(key)) : null;

    if (patternInfo) {
      formTradingName.tradingName = patternInfo.tradingName;
      isDraft.value = true;
    }

    // 状況に応じてページタイトル変更
    const pageTitle = computed(() => {
      if (patternInfo) {
        // 取引作成
        return root.$t('Label.New Trading');
      } else {
        // 取引フロー作成
        return lang === 'ja' ? '新規フロー / プロセス 作成' : 'Create Flow / Process List';
      }
    });

    const pageDesc = computed(() => {
      if (copyId.value) {
        return lang === 'ja'
          ? `Copy元の取引フローID: ${copyId.value}`
          : `Copied from ID: ${copyId.value}`;
      } else {
        return '';
      }
    });

    const query = {};
    if (tradingId !== 'new') query.tradingId = tradingId;

    const { loading, tradingInfo, error, fetchData } = useBffTradingManagesSearch(
      query,
      false,
      'new',
    );

    const managesEditorParams = {
      path: {},
      queries: {},
      immediate: false,
      tradingFlowId: 'new',
    };

    const {
      loading: editorLoading,
      tradingInfo: editorTradingInfo,
      processList,
      tmpProcessData,
      fetchData: fetchEditData,
    } = useBffTradingManagesEditor(managesEditorParams);

    const { onEventValidation, validationCheck } = useLakeelValidation(
      root.$store,
      formFlowName,
      formComponent,
    );

    const rules = {
      tradingManageName: [
        {
          lakeelMessageSourceFlg: true,
          validationId: 'tw-isMaxLength100',
          validator: onEventValidation,
          trigger: 'blur',
          field: 'tradingManageName',
          label: 'tradingManageName',
        },
      ],
      flowReference: [
        {
          lakeelMessageSourceFlg: true,
          validationId: 'tw-isMaxLength100',
          validator: onEventValidation,
          trigger: 'blur',
          field: 'flowReference',
          label: 'flowReference',
        },
      ],
      flowUserName: [
        {
          lakeelMessageSourceFlg: true,
          validationId: 'tw-isMaxLength100',
          validator: onEventValidation,
          trigger: 'blur',
          field: 'flowUserName',
          label: 'flowUserName',
        },
      ]
    };

    const filteredSchema = computed(() => {
      return processVisible.value
        ? tradingDetailTableFormNewSchema
        : tradingDetailTableFormNewSchema.filter(
            col => col.key !== 'processName',
          );
    });

    /* ========== Watch ========== */

    watch(error, newVal => {
      if (newVal && tradingId !== 'new') {
        root.$store
          .dispatch(
            'SHOW_ALERT',
            root.$t('Message.ERROR__FLOW_COUNT_CHECK_FAILED'),
          )
          .then(() => {
            root.$router.push(`/trading/?fid=${tradingId}`);
          });
      }
    });

    watch(tradingInfo, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        if (
          tradingId !== 'new' &&
          newVal.totalTradingFlowCount >= MAX_TRADING_FLOW_COUNT
        ) {
          showFlowLimitDialog();
          return;
        }
        formData.value = flatten(newVal.tradingFlowList, 'linkageInfo');
        fetchEditData({
          path: { companyId: companyId.value },
          queries: {
            tradingPatternId: newVal.tradingPatternId,
            tradingId: newVal.tradingId,
          },
        });
      }
    });

    watch(tmpProcessData, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        // フロー情報の準備g
        // コピー元がある場合はマージ
        if (copyId.value) {
          const copyTradingFlowList = _.find(
            editorTradingInfo.value.tradingFlowList,
            { tradingFlowId: copyId.value },
          );

          const overrideMap = {
            tmpProcessUserMemo: 'processUserMemo',
            tmpFromRoleId: 'fromRoleId',
            tmpFromCustomerId: 'fromCustomerId',
            tmpFromCompanyId: 'fromCompanyId',
            tmpFromCompanyShortName: 'fromCompanyShortName',
            tmpFromCompanyName: 'fromCompanyName',
            tmpFromCompanyIcon: 'fromCompanyIcon',
            tmpFromSectionId: 'fromSectionId',
            tmpFromSectionName: 'fromSectionName',
            tmpFromSectionShortName: 'fromSectionShortName',
            tmpToRoleId: 'toRoleId',
            tmpToCustomerId: 'toCustomerId',
            tmpToCompanyId: 'toCompanyId',
            tmpToCompanyName: 'toCompanyName',
            tmpToCompanyShortName: 'toCompanyShortName',
            tmpToCompanyIcon: 'toCompanyIcon',
            tmpToSectionId: 'toSectionId',
            tmpToSectionName: 'toSectionName',
            tmpToSectionShortName: 'toSectionShortName'
          };

          const overrideProcessData = applyOverridesFromTradingFlow(
            newVal,
            copyTradingFlowList,
            overrideMap,
          );

          tmpFlowInfo.value = createNewFlow(overrideProcessData);
          processData.value = mapToSchemaList(tmpFlowInfo.value);

          formFlowName.flowReference = copyTradingFlowList.flowReference;
          formFlowName.flowUserName = copyTradingFlowList.flowUserName;
        } else {
          tmpFlowInfo.value = createNewFlow(newVal);
          processData.value = mapToSchemaList(tmpFlowInfo.value);
        }
      }
    });

    watch(
      copyId,
      newCopyId => {
        // console.log('newFid: ', newCopyId);
      },
      { immediate: true },
    );

    watch(editorTradingInfo, newVal => {
      if (newVal.tradingSubmitStatusCd === 1) {
        isDraft.value = true;
      }
    });

    const isFwd = computed(() => {
      return root.$store.getters.isFwd;
    });

    /* ========== Methods ========== */

    // 取引フロー上限到達時のダイアログを表示します
    const showFlowLimitDialog = () => {
      root.$store
        .dispatch('SHOW_CONFIRM_CUSTOM', {
          message: root.$t('Message.FlowCountLimitReached', {
            max: MAX_TRADING_FLOW_COUNT,
          }),
          confirmButtonText: root.$t('Label.Create New Trading'),
          cancelButtonText: root.$t('Label.Close'),
        })
        .then(() => root.$router.push('/trading/new/')) // 新規取引作成画面へ
        .catch(() => root.$router.push(`/trading/?fid=${tradingId}`)); // 元の取引詳細画面へ
    };

    const assignMe = () => {
      formFlowName.flowUserName = root.$store.state.userProfile.userName;
    }

    // 指定したオブジェクトの中身を一階層上に展開
    const flatten = (ary, target) => {
      return _.map(ary, o => {
        return { ..._.omit(o, [target]), ...o.linkageInfo };
      });
    };

    const addFlow = flow => {
      formData.value.push(flow);
    };

    /**
     * 新規プロセスを返却します
     * @param { Object }  p プロセスモデル
     * @param { Boolean } plane 中身を空にするか
     */
    const createNewProcess = (p, plane) => {
      return {
        processId: p.tmpProcessId,
        processType: p.tmpProcessType,
        processName: p.tmpProcessName,
        processUserMemo: p.tmpProcessUserMemo,
        fromCompanyRoleCd: parseInt(p.tmpFromCompanyRoleCd),
        fromCustomerId: plane ? '' : p.tmpFromCustomerId,
        fromCompanyId: plane ? '' : p.tmpFromCompanyId,
        fromCompanyName: plane ? '' : p.tmpFromCompanyName,
        fromCompanyShortName: plane ? '' : p.tmpFromCompanyShortName,
        fromSectionId: plane ? '' : p.tmpFromSectionId,
        fromSectionName: plane ? '' : p.tmpFromSectionName,
        fromSectionShortName: plane ? '' : p.tmpFromSectionShortName,
        toCompanyRoleCd: parseInt(p.tmpToCompanyRoleCd),
        toCustomerId: plane ? '' : p.tmpToCustomerId,
        toCompanyId: plane ? '' : p.tmpToCompanyId,
        toCompanyName: plane ? '' : p.tmpToCompanyName,
        toCompanyShortName: plane ? '' : p.tmpToCompanyShortName,
        toSectionId: plane ? '' : p.tmpToSectionId,
        toSectionName: plane ? '' : p.tmpToSectionName,
        toSectionShortName: plane ? '' : p.tmpToSectionShortName,
        uuid: generateUuid(),
        editableFlg: EDITABLE_FLG.ON,
        deletableFlg: DELETE_ABLE_FLG.OK,
      };
    };

    // 新しいフローを返却します
    const createNewFlow = tmpFlow => {
      const g = _.groupBy(tmpFlow, 'tmpEntityType');
      const entityStatus = _.map(g, ary => {
        return {
          entityTypeCd: ary[0].tmpEntityType,
          processStatus: _.map(ary, p => {
            return createNewProcess(p);
          }),
        };
      });
      return {
        tradingManageName: '',
        flowUuid: generateUuid(),
        entityStatus: orderEntities(entityStatus),
        editableFlg: EDITABLE_FLG.ON,
        deletableFlg: DELETE_ABLE_FLG.OK,
        visibleFlg: VISIBLE_FLG.ON,
        unAddableArea: tmpFlow.unAddableArea || [],
        tradingChannelInfo: {
          flowType: tmpFlow.tmpFlowType,
          channelFlowNo: 0, // PB-1204では一つのフロー作成のみの画面要件なので0
          deleteChannel: undefined, // Ph6時点要件なし。現時点未使用
        },
      };
    };

    /**
     * data: 元配列（tmpEntityType, tmpProcessId を持つ）
     * tradingFlow: { entityStatus: [{ entityTypeCd, processStatus: [{ processId, ... }] }] }
     * overrideMap: { targetKeyInItem: sourceKeyInProcessStatus }
     */
    const applyOverridesFromTradingFlow = (data, tradingFlow, overrideMap) => {
      // 1) ルックアップ用マップ作成: "entityTypeCd-processId" -> processStatus行
      const statusMap = new Map();
      (tradingFlow?.entityStatus || []).forEach(entity => {
        const type = entity?.entityTypeCd;
        (entity?.processStatus || []).forEach(ps => {
          if (ps && ps.processId != null) {
            statusMap.set(`${type}-${ps.processId}`, ps);
          }
        });
      });
      // statusMap に存在して data に存在しないキーを取得
      const dataKeySet = new Set(
        (data || []).map(
          item => `${item.tmpEntityType}-${item.tmpProcessId}`,
        ),
      );
      const notInData = [...statusMap.keys()]
        .filter(key => !dataKeySet.has(key))
        .map(key => {
          const [entityTypeCd, processId] = key.split('-');
          return { entityTypeCd: Number(entityTypeCd), processId };
        });
      // notInData のメンバーを data に追加
      notInData.forEach(({ entityTypeCd, processId }) => {
        const ps = statusMap.get(`${entityTypeCd}-${processId}`);
        const processType = ps?.processType || '';
        const processName =
          processType === 'PT_TPATPRB'
            ? _.get(_.find(processNamesMap, { processId }), 'nameEn')
            : _.get(
                _.find(processNamesMap, { processType }),
                'nameEn',
              );
        data.push({
          tmpEntityType: entityTypeCd,
          tmpProcessType: processType,
          tmpProcessId: processId,
          tmpProcessName: processName,
          tmpProcessUserMemo: '',
          tmpFromRoleId: '',
          tmpFromCustomerId: '',
          tmpFromCompanyId: '',
          tmpFromCompanyShortName: '',
          tmpFromCompanyName: '',
          tmpFromCompanyIcon: '',
          tmpFromSectionId: '',
          tmpFromSectionName: '',
          tmpFromSectionShortName: '',
          tmpToRoleId: '',
          tmpToCustomerId: '',
          tmpToCompanyId: '',
          tmpToCompanyName: '',
          tmpToCompanyShortName: '',
          tmpToCompanyIcon: '',
          tmpToSectionId: '',
          tmpToSectionName: '',
          tmpToSectionShortName: '',
        });
      });
      // 2) 一致するものだけ残して 3) overrideMap に従って上書き
      return (data || [])
        .filter(item =>
          statusMap.has(`${item.tmpEntityType}-${item.tmpProcessId}`),
        )
        .map(item => {
          const status = statusMap.get(
            `${item.tmpEntityType}-${item.tmpProcessId}`,
          );
          const updated = { ...item };
          for (const [targetKey, sourceKey] of Object.entries(
            overrideMap || {},
          )) {
            if (sourceKey in status && status[sourceKey] !== undefined) {
              updated[targetKey] = status[sourceKey];
            }
          }
          return updated;
        });
    };

    // 指定されていないセクションがあるかを返却します
    function hasEmptySections() {
      return !!formProcessComponent.value?.$el.querySelector('.invalid');
    }

    // 削除したプロセスがあればコンファームを出します
    const confirmDeleteProcess = async () => {
      const count = _.size(tmpFlowInfo.value?.deleteProcessIds);
      if (count === 0) {
        return true;
      } else {
        return await root.$store
          .dispatch(
            'SHOW_CONFIRM',
            root.$t('Message.CONFIRM__PROCESS_DELETE'),
          )
          .then(() => true)
          .catch(() => false);
      }
    };

    const handleCancel = () => {
      root.$store
        .dispatch(
          'SHOW_CONFIRM',
          root.$t('Message.CONFIRM__NAVIGATION_WITH_UNSAVED_DATA'),
        )
        .then(async () => {
          const path = tradingId === 'new' ? `/trading` : `/trading?fid=${tradingId}`;
          root.$router.push(path);
        })
        .catch(() => {});
    };

    const handleSave = async () => {
      if ((await confirmDeleteProcess()) === false) {
        return;
      }
      // 登録時のローディング画面表示
      root.$store.commit('START_PROCESS');
      formData.value = [];
      formData.value.push(tmpFlowInfo.value);

      // エラーがなければ送信データを組み立て
      const tradingFlowList = _.map(formData.value, flow => {
        const entityStatus = _.map(flow.entityStatus, s => {
          return {
            ...s,
            provisionalId: s.entityId ? undefined : generateUuid(20),
          };
        });
        return {
          ...flow,
          entityStatus: entityStatus,
        };
      });

      // 新しい取引フロー名を追加
      tradingFlowList[0].tradingManageName = formFlowName.tradingManageName;
      tradingFlowList[0].flowReference = formFlowName.flowReference;
      tradingFlowList[0].flowUserName = formFlowName.flowUserName;

      const data = {
        tradingInfo: {
          tradingId: editorTradingInfo.value.tradingId,
          tradingCreateUserId: editorTradingInfo.value.tradingCreateUserId,
          tradingCreateUserName: editorTradingInfo.value.tradingCreateUserName,
          tradingCreateUserIcon: editorTradingInfo.value.tradingCreateUserIcon,
          tradingCreateDate: editorTradingInfo.value.tradingCreateDate,
          tradingUpdateUserId: editorTradingInfo.value.tradingUpdateUserId,
          tradingUpdateDate: editorTradingInfo.value.tradingUpdateDate,
          tradingTrx: editorTradingInfo.value.tradingTrx,
          deleteTradingFlowIds: deleteTradingFlowIds.value,
          tradingName: formTradingName.tradingName,
          tradingPatternInfo: editorTradingInfo.value.tradingPatternInfo,
          tradingFlowList: tradingFlowList,
        },
      };

      const params = {
        // bff_td_10 取引管理新規登録BFF
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode: 'post_/v1/tradings/{companyId}/manages/wk',
          path: {
            companyId: companyId.value,
          },
        },
        data: data,
      };

      $api
        .request(params)
        .then(res => {
          root.$store.commit('END_PROCESS');
          root.$store.dispatch('SHOW_COMPLETED');
          const tradingInfo = res.tradingInfo;
          const tradingFlowId = _.differenceBy(
            tradingInfo.tradingFlowList,
            tradingFlowList,
            'tradingFlowId',
          ).map(flow => flow.tradingFlowId);

          const path = `/trading/process/${tradingInfo.tradingId}/${tradingFlowId[0]}/`;
          root.$router.push(path);
        })
        .catch(err => {
          root.$store.commit('END_PROCESS');
          if (err.response?.errors?.[0]?.code === 'ETNC0214') {
            root.$store.dispatch('SHOW_ALERT', root.$t('Message.ERROR__DATA_CONFLICT'));
          } else {
            root.$store.dispatch('SHOW_ALERT', err.message);
          }
        });
    };

    const handleRegister = async () => {
      // root.$store.dispatch(
      //   'SHOW_ALERT',
      //   this.$t('Message.ERROR__INPUT_INVALID'),
      // );

      if (hasEmptySections()) {
        root.$store.dispatch(
          'SHOW_ALERT',
          root.$t('Message.ERROR__INPUT_INVALID'),
        );
        return;
      }
      if ((await confirmDeleteProcess()) === false) {
        return;
      }
      // 登録時のローディング画面表示
      root.$store.commit('START_PROCESS');

      const result = await validationCheck();
      if (!result) {
        root.$store.commit('END_PROCESS');
        root.$store.dispatch(
          'SHOW_ALERT',
          root.$t('Message.ERROR__INPUT_INVALID'),
        );
      }
      if (!formData.value) formData.value = [];
      formData.value.push(tmpFlowInfo.value);

      // エラーがなければ送信データを組み立て
      const tradingFlowList = _.map(formData.value, flow => {
        const entityStatus = _.map(flow.entityStatus, s => {
          return {
            ...s,
            provisionalId: s.entityId ? undefined : generateUuid(20),
          };
        });
        return {
          ...flow,
          entityStatus: entityStatus,
        };
      });
      // 新しい取引フロー名を追加
      tradingFlowList[formData.value.length - 1].tradingManageName = formFlowName.tradingManageName;
      tradingFlowList[formData.value.length - 1].flowReference = formFlowName.flowReference;
      tradingFlowList[formData.value.length - 1].flowUserName = formFlowName.flowUserName;
      tradingFlowList[formData.value.length - 1].copyTradingFlowId = copyId.value || undefined;

      const data = {
        tradingInfo: {
          tradingId: editorTradingInfo.value.tradingId,
          tradingCreateUserId: editorTradingInfo.value.tradingCreateUserId,
          tradingCreateUserName: editorTradingInfo.value.tradingCreateUserName,
          tradingCreateUserIcon: editorTradingInfo.value.tradingCreateUserIcon,
          tradingCreateDate: editorTradingInfo.value.tradingCreateDate,
          tradingUpdateUserId: editorTradingInfo.value.tradingUpdateUserId,
          tradingUpdateDate: editorTradingInfo.value.tradingUpdateDate,
          tradingTrx: editorTradingInfo.value.tradingTrx,
          deleteTradingFlowIds: props.deleteTradingFlowIds,
          tradingName: isDraft.value
            ? formTradingName.tradingName
            : editorTradingInfo.value.tradingName,
          tradingPatternInfo: editorTradingInfo.value.tradingPatternInfo,
          tradingFlowList: tradingFlowList,
        },
      };

      const params = {
        // bff_td_10 取引管理新規登録BFF
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode: 'post_/v1/tradings/{companyId}/manages',
          path: {
            companyId: companyId.value,
          },
        },
        data: data,
      };

      $api
        .request(params)
        .then(res => {
          root.$store.commit('END_PROCESS');
          root.$store.dispatch('SHOW_COMPLETED');
          const tradingInfo = res.tradingInfo;
          const tradingFlowId = _.differenceBy(
            tradingInfo.tradingFlowList,
            tradingFlowList,
            'tradingFlowId',
          ).map(flow => flow.tradingFlowId);

          const path = `/trading/process/${tradingInfo.tradingId}/${tradingFlowId[0]}/`;
          root.$router.push(path);
        })
        .catch(err => {
          root.$store.commit('END_PROCESS');
          if (err.response?.errors?.[0]?.code === 'ETNC0214') {
            root.$store.dispatch('SHOW_ALERT', root.$t('Message.ERROR__DATA_CONFLICT'));
          } else {
            root.$store.dispatch('SHOW_ALERT', err.message);
          }
        });
    };

    const updateFlowValue = payload => {
      const { row, prop, value } = payload;
      const processId = row.processId;

      for (const entity of tmpFlowInfo.value.entityStatus) {
        const target = entity.processStatus.find(
          p => p.processId === processId,
        );
        if (target) {
          if (prop === 'from' || prop === 'to') {
            if (value.sectionId.startsWith(SECTION_PREFIX.SECTION_PREFEX)) {
              target[`${prop}CustomerId`] = value.tradingId;
              target[`${prop}CompanyId`] = null;
            } else {
              target[`${prop}CompanyId`] = value.tradingId;
              target[`${prop}CustomerId`] = null;
            }
            target[`${prop}CompanyShortName`] = value.tradingShortName;
            target[`${prop}CompanyName`] = value.tradingName;
            target[`${prop}SectionId`] = value.sectionId;
            target[`${prop}SectionName`] = value.sectionName;
            target[`${prop}SectionShortName`] = value.sectionShortName;
            isDirty.value = true;
          } else {
            target[prop] = value;
            isDirty.value = true;
          }
          break;
        }
      }
      processData.value = mapToSchemaList(tmpFlowInfo.value);
    };

    // 採番したprocessIdを返却します
    const getProcessId = processId => {
      if (
        !['ECREQ', 'ICREQ', 'TPBLI', 'FDCOR', 'FDCOI', 'MIRIP'].includes(
          processId,
        )
      ) {
        return processId;
      }
      const filtered = _.filter(processData.value, item => {
        return (
          item.processId.startsWith(processId) &&
          !(
            item.eventCancel === PROCESS_CANCEL_FLG.ON &&
            [
              PROCESS_PROGRESS_STATUS.AGREED,
              PROCESS_PROGRESS_STATUS.DONE,
            ].includes(item.processProgressStatusCd)
          )
        );
      });
      let ret;
      _.times(MAX_PROCESS_NUMBER[processId], i => {
        if (!_.find(filtered, { processId: `${processId}0${i + 1}` }) && !ret) {
          ret = `${processId}0${i + 1}`;
        }
      });
      if (!ret) {
        ret = `${processId}01`;
      }
      return ret;
    };

    const setProcess = (o, p) => {
      const flow = _.find(this.form.items, item => {
        return (
          (item.tradingFlowId && item.tradingFlowId === p.tradingFlowId) ||
          (item.flowUuid && item.flowUuid === p.flowUuid)
        );
      });
      const process = _.find(
        _.find(flow.entityStatus, { entityTypeCd: p.entityTypeCd })
          .processStatus,
        o => {
          return (
            o.processId === p.processId &&
            (o.processSeq ? o.processSeq === p.processSeq : o.uuid === p.uuid)
          );
        },
      );
      if (o.sectionId.startsWith(SECTION_PREFIX.SECTION_PREFEX)) {
        process[`${p.fromTo}CustomerId`] = o.tradingId;
        process[`${p.fromTo}CompanyId`] = null;
      } else {
        process[`${p.fromTo}CompanyId`] = o.tradingId;
        process[`${p.fromTo}CustomerId`] = null;
      }
      process[`${p.fromTo}CompanyShortName`] = o.tradingShortName;
      process[`${p.fromTo}CompanyName`] = o.tradingName;
      process[`${p.fromTo}SectionId`] = o.sectionId;
      process[`${p.fromTo}SectionName`] = o.sectionName;
      process[`${p.fromTo}SectionShortName`] = o.sectionShortName;
      isDirty.value = true;
    };

    const addProcess = process => {
      const entityTypeCd = process[0];
      const processId = getProcessId(process[1]);
      const processType = _.get(processNamesMap[process[1]], 'processType');

      const flow = tmpFlowInfo.value;
      const processName =
        processType === 'PT_TPATPRB'
          ? _.get(_.find(processNamesMap, { processId }), 'nameEn')
          : _.get(
              _.find(processNamesMap, {
                processType,
              }),
              'nameEn',
            );
      const newProcess = {
        processType,
        processId,
        processName,
        processUserMemo: '',
        fromCompanyRoleCd: '',
        fromCustomerId: '',
        fromCompanyId: '',
        fromSectionId: '',
        fromSectionName: '',
        toCompanyRoleCd: '',
        toCustomerId: '',
        toCompanyId: '',
        toSectionId: '',
        toSectionName: '',
        uuid: generateUuid(),
        editableFlg: EDITABLE_FLG.ON,
        deletableFlg: DELETE_ABLE_FLG.OK,
      };

      const entityStatus = _.find(flow.entityStatus, {
        entityTypeCd,
      });
      if (entityStatus) {
        entityStatus.processStatus.push(newProcess);
      } else {
        flow.entityStatus.push({
          entityTypeCd,
          processStatus: [newProcess],
        });
      }
      const ordered = orderProcesses(orderEntities(flow.entityStatus));
      flow.entityStatus.splice(0, flow.entityStatus.length);
      flow.entityStatus.push(...ordered);
      processData.value = mapToSchemaList(flow);
    };

    const removeProcess = payload => {
      const { process: p } = payload;
      const entity = _.find(tmpFlowInfo.value.entityStatus, {
        entityTypeCd: p.entityTypeCd,
      });

      const flowType = _.get(tmpFlowInfo.value, 'tradingChannelInfo.flowType');

      // 三国間取引のフローかつ、フロー内のエンティティが1つで、かつそのエンティティのプロセスが1つの場合、フローを削除するかどうかを確認します
      if (
        flowType &&
        tmpFlowInfo.value.entityStatus.length === 1 &&
        entity.processStatus.length === 1
      ) {
        // this.removeFlow(flow);
      } else {
        const index = _.findIndex(entity.processStatus, o => {
          return (
            o.processId === p.processId &&
            (o.processSeq ? o.processSeq === p.processSeq : o.uuid === p.uuid)
          );
        });

        if (!tmpFlowInfo.value.deleteProcessIds) {
          tmpFlowInfo.value.deleteProcessIds = [];
        }

        tmpFlowInfo.value.deleteProcessIds.push({
          entityId: p.entityId,
          deleteProcessId: p.processId,
          deleteProcessSeq: p.processSeq,
        });

        entity.processStatus.splice(index, 1);
        if (_.isEmpty(entity.processStatus)) {
          tmpFlowInfo.value.entityStatus.splice(
            _.findIndex(tmpFlowInfo.value.entityStatus, {
              entityTypeCd: p.entityTypeCd,
            }),
            1,
          );
        }

        if (_.isEmpty(tmpFlowInfo.value.entityStatus)) {
          const tradingFlowId = tmpFlowInfo.value.tradingFlowId;
          // tmpFlowInfo.value.splice(_.findIndex(this.form.items, flow), 1);
          if (tradingFlowId) {
            deleteTradingFlowIds.value.push(tradingFlowId);
          }
        }

        processData.value = mapToSchemaList(tmpFlowInfo.value);
        isDirty.value = true;
      }
    };

    onMounted(() => {
      if (!root.$route.query.p) {
        fetchData();
      } else {
        fetchEditData({
          path: { companyId: companyId.value },
          queries: {
            tradingPatternId: patternInfo.tradingPatternId,
          },
        });
      }
    });

    return {
      lang,
      pageTitle,
      pageDesc,
      formComponent,
      formProcessComponent,
      rules,
      loading,
      editorLoading,
      processVisible,
      tradingInfo,
      processList,
      formTradingName,
      formFlowName,
      tmpFlowInfo,
      formData,
      filteredSchema,
      processData,
      assignMe,
      handleSave,
      handleRegister,
      handleCancel,
      updateFlowValue,
      addProcess,
      removeProcess,
      onEventValidation,
      copyId,
      isFwd,
      isDraft
    };
  },
});
</script>

<style lang="scss" scoped>
.process-show-switch-form {
  margin-top: 10px;
  margin-right: 20px;
  display: flex;
  flex-direction: column;
  position: relative;

  & label {
    position: absolute;
    top: -20px;
    left: 28px;
    display: block;
    text-align: center;
    font-size: 12px;
    margin-bottom: 2px;
  }
  & .process-show-switch {
    &::v-deep .el-switch__label {
      & span {
        white-space: pre-wrap;
      }
      &.el-switch__label--right,
      &.el-switch__label--left {
        position: relative;
        width: 20px;
      }
      & .tw-icon-not-view {
        position: absolute;
        top: 0px;
        width: 20px;
        height: 20px;
        background: url(../../../../assets/images/icon_eye.png) no-repeat 0 0;
        background-size: 20px auto;
        background-position: 0 bottom;
      }
      & .tw-icon-view {
        position: absolute;
        top: 0px;
        width: 20px;
        height: 20px;
        background: url(../../../../assets/images/icon_eye.png) no-repeat 0 0;
        background-size: 20px auto;
      }
    }
  }
}
</style>
